import React, { useState, useEffect } from 'react';
import {
  RefreshCw, Search, Package, AlertTriangle, CheckCircle, X,
  ChevronDown, ChevronUp, Download,
  XCircle, Info, CheckCircle2, Code, Eye, ArrowRight, Plus, Edit3
} from 'lucide-react';
import { authFetch } from "../utils/authFetch";

const ProductosAB = ({ token }) => {
  const API_URL = process.env.REACT_APP_API_URL
    || (window.location.hostname === 'localhost'
      ? 'http://localhost:3001/api'
      : 'https://alvarezbohlback-production.up.railway.app/api');

  // --- Estados principales ---
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncExistentesLoading, setSyncExistentesLoading] = useState(false);
  const [productosAB, setProductosAB] = useState(null);
  const [rawApiData, setRawApiData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('pendientes');
  const [expandedProduct, setExpandedProduct] = useState(null);

  // --- Estados de selección y modal ---
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [modalTab, setModalTab] = useState('preview'); // 'preview' | 'debug'
  const [syncResult, setSyncResult] = useState(null);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncStrategy, setSyncStrategy] = useState('replace'); // 'replace' | 'sum'
  const [debugSelectedProduct, setDebugSelectedProduct] = useState(null);

  // ======= CARGA DE DATOS =======
  const loadProductosAB = async () => {
    setLoading(true);
    setSelectedProducts([]);
    try {
      const response = await authFetch(`${API_URL}/productos-ab`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        console.log('=== RAW API AB RESPONSE ===');
        console.log(JSON.stringify(result.data.raw_api_data, null, 2));
        setProductosAB(result.data);
        setRawApiData(result.data.raw_api_data || {});
      } else {
        alert('Error al cargar productos: ' + result.error);
      }
    } catch (err) {
      console.error('Error loading productos AB:', err);
      alert('Error de conexión al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (token) loadProductosAB(); }, [token]);

  const handleSyncExistentes = async () => {
    if (!window.confirm('¿Sincronizar datos de AB para productos existentes?\n\nSe actualizarán: costo promedio, precio compra, comprometido, disponible, factores y almacén.')) return;
    setSyncExistentesLoading(true);
    try {
      const response = await authFetch(`${API_URL}/productos-ab/sync-existentes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(`Sincronización completada: ${result.data.actualizados} productos actualizados`);
        await loadProductosAB();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error syncing:', err);
      alert('Error al sincronizar');
    } finally {
      setSyncExistentesLoading(false);
    }
  };

  // ======= SELECCIÓN =======
  const pendientes = productosAB?.productos_pendientes || [];
  const existentes = productosAB?.productos_existentes || [];

  const getFilteredProducts = (list) => {
    if (!list) return [];
    return list.filter(p => {
      const term = searchTerm.toLowerCase();
      return !term ||
        (p.codigo || '').toLowerCase().includes(term) ||
        (p.nombre || '').toLowerCase().includes(term) ||
        (p.proveedor || '').toLowerCase().includes(term);
    });
  };

  const filteredPendientes = getFilteredProducts(pendientes);
  const filteredExistentes = getFilteredProducts(existentes);

  const toggleSelectProduct = (codigo) => {
    setSelectedProducts(prev =>
      prev.includes(codigo)
        ? prev.filter(c => c !== codigo)
        : [...prev, codigo]
    );
  };

  const getCurrentFilteredList = () => {
    return filterTab === 'pendientes' ? filteredPendientes : filteredExistentes;
  };

  const toggleSelectAll = () => {
    const filteredCodigos = getCurrentFilteredList().map(p => p.codigo);
    const allSelected = filteredCodigos.every(c => selectedProducts.includes(c));
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(c => !filteredCodigos.includes(c)));
    } else {
      setSelectedProducts(prev => [...new Set([...prev, ...filteredCodigos])]);
    }
  };

  const currentList = getCurrentFilteredList();
  const allFilteredSelected = currentList.length > 0 &&
    currentList.every(p => selectedProducts.includes(p.codigo));

  // ======= MODAL SYNC =======
  const getSelectedProductsData = () => {
    // Busca en pendientes Y existentes
    const all = [...pendientes, ...existentes];
    return all.filter(p => selectedProducts.includes(p.codigo));
  };

  const handleOpenSyncModal = async () => {
    if (selectedProducts.length === 0) {
      alert('Selecciona al menos un producto para sincronizar');
      return;
    }
    setSyncResult(null);
    setSyncPreview(null);
    setModalTab('preview');
    setDebugSelectedProduct(null);
    setSyncStrategy('replace');
    setShowSyncModal(true);

    // Llamar al endpoint en modo preview
    setPreviewLoading(true);
    try {
      const productosToSync = getSelectedProductsData();
      const response = await authFetch(`${API_URL}/productos-ab/sincronizar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ productos: productosToSync, mode: 'preview' })
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setSyncPreview(result.data);
      } else {
        alert('Error al obtener preview: ' + result.error);
      }
    } catch (err) {
      console.error('Error obteniendo preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseSyncModal = () => {
    setShowSyncModal(false);
    if (syncResult) {
      loadProductosAB();
    }
  };

  const handleConfirmSync = async () => {
    const productosToSync = getSelectedProductsData();
    const hasLotesConCambios = syncPreview?.some(p => p.tiene_lotes_con_cambios);
    const strategyMsg = hasLotesConCambios ? `\nEstrategia para lotes existentes: ${syncStrategy === 'sum' ? 'SUMAR cantidades' : 'REEMPLAZAR cantidades'}` : '';
    if (!window.confirm(`¿Sincronizar ${productosToSync.length} producto(s) de AB al WMS?${strategyMsg}`)) return;

    setSyncLoading(true);
    setSyncResult(null);
    try {
      const response = await authFetch(`${API_URL}/productos-ab/sincronizar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          productos: productosToSync,
          mode: 'execute',
          strategy: syncStrategy
        })
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setSyncResult(result.data);
        setSelectedProducts([]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error sincronizando:', err);
      alert('Error al sincronizar productos');
    } finally {
      setSyncLoading(false);
    }
  };

  // ======= HELPERS =======
  const getValidationStatus = (prod) => {
    const issues = [];
    if (!prod.codigo_barras_minima && !prod.codigo_barras_mayor) issues.push('Sin barcode');
    if (!prod.unidad_mayor) issues.push('Sin unidad mayor');
    if (!prod.unidad_base) issues.push('Sin unidad base');
    if (!prod.lotes || prod.lotes.length === 0) issues.push('Sin lotes');
    return { ok: issues.length === 0, issues };
  };

  // ======= RENDER: MODAL DE SINCRONIZACIÓN =======
  const renderSyncModal = () => {
    if (!showSyncModal) return null;
    const selectedData = getSelectedProductsData();
    const hasLotesConCambios = syncPreview?.some(p => p.tiene_lotes_con_cambios);

    return (
      <div className="sync-modal-overlay" onClick={handleCloseSyncModal}>
        <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="sync-modal-header">
            <h2>
              <Package size={20} />
              Vista Previa de Sincronización
            </h2>
            <button className="modal-close" onClick={handleCloseSyncModal}>
              <X size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="sync-modal-tabs">
            <button
              className={`modal-tab ${modalTab === 'preview' ? 'active' : ''}`}
              onClick={() => setModalTab('preview')}
            >
              <Eye size={14} />
              Vista Previa ({selectedData.length})
            </button>
            <button
              className={`modal-tab debug ${modalTab === 'debug' ? 'active' : ''}`}
              onClick={() => setModalTab('debug')}
            >
              <Code size={14} />
              Debug JSON
            </button>
          </div>

          {/* Body */}
          <div className="sync-modal-body">
            {/* VISTA PREVIA - con datos del servidor */}
            {modalTab === 'preview' && !syncResult && (
              <div className="preview-list">
                {previewLoading && (
                  <div className="ab-loading" style={{padding: '20px'}}>
                    <RefreshCw size={24} className="spin" />
                    <p>Analizando productos...</p>
                  </div>
                )}

                {!previewLoading && syncPreview && syncPreview.map((item, idx) => {
                  const prod = selectedData.find(p => p.codigo?.trim() === item.codigo) || {};
                  const validation = getValidationStatus(prod);

                  return (
                    <div key={idx} className={`preview-item ${item.tipo === 'nuevo' ? (validation.ok ? 'ok' : 'warning') : 'existing'}`}>
                      <div className="preview-item-header">
                        <div className="preview-item-left">
                          {item.tipo === 'nuevo'
                            ? <Plus size={16} className="icon-new" />
                            : <Edit3 size={16} className="icon-edit" />
                          }
                          <span className="preview-code">{item.codigo}</span>
                          <span className="preview-name">{item.nombre}</span>
                        </div>
                        <span className={`preview-type-badge ${item.tipo}`}>
                          {item.tipo === 'nuevo' ? 'Nuevo' : 'Existente'}
                        </span>
                      </div>

                      {/* PRODUCTO NUEVO */}
                      {item.tipo === 'nuevo' && (
                        <>
                          <div className="preview-item-grid">
                            <div className="preview-field">
                              <span className="field-label">BC Mayor</span>
                              <span className={`field-value ${!prod.codigo_barras_mayor ? 'missing' : ''}`}>
                                {prod.codigo_barras_mayor || '-'}
                              </span>
                            </div>
                            <div className="preview-field">
                              <span className="field-label">BC Intermedia</span>
                              <span className={`field-value ${!prod.codigo_barras_intermedia ? 'missing' : ''}`}>
                                {prod.codigo_barras_intermedia || '-'}
                              </span>
                            </div>
                            <div className="preview-field">
                              <span className="field-label">BC Mínima</span>
                              <span className={`field-value ${!prod.codigo_barras_minima ? 'missing' : ''}`}>
                                {prod.codigo_barras_minima || '-'}
                              </span>
                            </div>
                            <div className="preview-field">
                              <span className="field-label">Jerarquía</span>
                              <span className="field-value">
                                {(prod.jerarquia || []).join(' → ') || '?'}
                              </span>
                            </div>
                            <div className="preview-field">
                              <span className="field-label">Proveedor</span>
                              <span className="field-value">{prod.proveedor || '-'}</span>
                            </div>
                            <div className="preview-field">
                              <span className="field-label">Familia</span>
                              <span className="field-value">{prod.familia || '-'}</span>
                            </div>
                          </div>

                          {item.lotes_ab?.length > 0 && (
                            <div className="preview-lotes">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Lote</th>
                                    <th>Vencimiento</th>
                                    <th>Mayor</th>
                                    <th>Display</th>
                                    <th>Base</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.lotes_ab.map((l, li) => (
                                    <tr key={li}>
                                      <td>{l.lote}</td>
                                      <td>{l.fecha_expiracion || '-'}</td>
                                      <td className="num">{l.cantidad_mayor}</td>
                                      <td className="num">{l.cantidad_intermedia}</td>
                                      <td className="num">{l.cantidad_minima}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {!validation.ok && (
                            <div className="preview-warnings">
                              <AlertTriangle size={12} />
                              <span>{validation.issues.join(' | ')}</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* PRODUCTO EXISTENTE - Comparación de lotes */}
                      {item.tipo === 'existente' && (
                        <div className="preview-comparison">
                          <div className="comparison-summary">
                            <span>WMS: {item.lotes_wms} lote(s)</span>
                            <span>AB: {item.lotes_ab} lote(s)</span>
                          </div>

                          {item.comparacion?.length > 0 && (
                            <div className="preview-lotes">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Lote</th>
                                    <th>Estado</th>
                                    <th>WMS (May/Dsp/Base)</th>
                                    <th></th>
                                    <th>AB (May/Dsp/Base)</th>
                                    <th>Detalle</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.comparacion.map((comp, ci) => (
                                    <tr key={ci} className={`comp-row ${comp.estado}`}>
                                      <td><strong>{comp.lote}</strong></td>
                                      <td>
                                        {comp.estado === 'nuevo' && <span className="badge-new">Nuevo</span>}
                                        {comp.estado === 'cambios' && <span className="badge-changes">Cambios</span>}
                                        {comp.estado === 'sin_cambios' && <span className="badge-ok">OK</span>}
                                      </td>
                                      <td className="num">
                                        {comp.wms
                                          ? `${comp.wms.cantidad_max} / ${comp.wms.cantidad_med} / ${comp.wms.cantidad_peq}`
                                          : '-'
                                        }
                                      </td>
                                      <td className="arrow-cell">
                                        {comp.estado !== 'sin_cambios' && <ArrowRight size={14} />}
                                      </td>
                                      <td className="num">
                                        {comp.ab
                                          ? `${comp.ab.cantidad_max} / ${comp.ab.cantidad_med} / ${comp.ab.cantidad_peq}`
                                          : '-'
                                        }
                                      </td>
                                      <td className="comp-msg">{comp.mensaje}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* No hay preview aún (fallback si no se cargó) */}
                {!previewLoading && !syncPreview && selectedData.map((prod, idx) => {
                  const validation = getValidationStatus(prod);
                  return (
                    <div key={idx} className={`preview-item ${validation.ok ? 'ok' : 'warning'}`}>
                      <div className="preview-item-header">
                        <div className="preview-item-left">
                          <span className="preview-code">{prod.codigo}</span>
                          <span className="preview-name">{prod.nombre}</span>
                        </div>
                        <span className="preview-lotes-badge">{prod.lotes?.length || 0} lotes</span>
                      </div>
                    </div>
                  );
                })}

                {/* Selector de estrategia solo si hay lotes existentes con cambios */}
                {hasLotesConCambios && (
                  <div className="strategy-selector">
                    <div className="strategy-label">
                      <Info size={14} />
                      <strong>Estrategia para lotes existentes con cambios:</strong>
                    </div>
                    <div className="strategy-options">
                      <label className={`strategy-option ${syncStrategy === 'replace' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="strategy"
                          value="replace"
                          checked={syncStrategy === 'replace'}
                          onChange={() => setSyncStrategy('replace')}
                        />
                        <div>
                          <strong>Reemplazar</strong>
                          <span>Las cantidades de AB reemplazan las actuales del WMS</span>
                        </div>
                      </label>
                      <label className={`strategy-option ${syncStrategy === 'sum' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="strategy"
                          value="sum"
                          checked={syncStrategy === 'sum'}
                          onChange={() => setSyncStrategy('sum')}
                        />
                        <div>
                          <strong>Sumar</strong>
                          <span>Las cantidades de AB se suman a las actuales del WMS</span>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DEBUG JSON */}
            {modalTab === 'debug' && (
              <div className="debug-container">
                <div className="debug-info-bar">
                  <Info size={14} />
                  <span>JSON crudo de la API de AB — Verifica nombres de campos y valores</span>
                </div>

                <div className="debug-product-selector">
                  <label>Producto:</label>
                  <select
                    value={debugSelectedProduct || ''}
                    onChange={(e) => setDebugSelectedProduct(e.target.value || null)}
                  >
                    <option value="">— Todos los seleccionados —</option>
                    {selectedData.map((p, i) => (
                      <option key={i} value={p.codigo}>{p.codigo} - {p.nombre}</option>
                    ))}
                  </select>
                </div>

                <pre className="debug-json">
                  {debugSelectedProduct
                    ? JSON.stringify(rawApiData[debugSelectedProduct] || { error: 'No encontrado en raw data' }, null, 2)
                    : JSON.stringify(
                        selectedData.map(p => rawApiData[p.codigo] || { Codigo: p.codigo, error: 'No encontrado' }),
                        null, 2
                      )
                  }
                </pre>
              </div>
            )}

            {/* RESULTADO DE SINCRONIZACIÓN */}
            {syncResult && (
              <div className="sync-result">
                <div className="sync-result-header">
                  <h3>Resultado de la Sincronización</h3>
                  <div className="sync-result-stats">
                    {syncResult.creados > 0 && (
                      <span className="result-stat ok">
                        <Plus size={14} /> {syncResult.creados} creados
                      </span>
                    )}
                    {syncResult.actualizados > 0 && (
                      <span className="result-stat updated">
                        <Edit3 size={14} /> {syncResult.actualizados} actualizados
                      </span>
                    )}
                    {syncResult.sin_cambios > 0 && (
                      <span className="result-stat skip">
                        <Info size={14} /> {syncResult.sin_cambios} sin cambios
                      </span>
                    )}
                    {syncResult.errores > 0 && (
                      <span className="result-stat error">
                        <XCircle size={14} /> {syncResult.errores} errores
                      </span>
                    )}
                  </div>
                </div>

                <div className="sync-result-table-container">
                  <table className="sync-result-table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Producto</th>
                        <th>Estado</th>
                        <th>Lote</th>
                        <th>Ubicación</th>
                        <th>Stock</th>
                        <th>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(syncResult.detalles || []).map((det, idx) => {
                        const lotes = det.lotes_info || [];
                        if (lotes.length === 0) {
                          return (
                            <tr key={idx} className={`result-row ${det.estado}`}>
                              <td><strong>{det.codigo}</strong></td>
                              <td>{det.nombre}</td>
                              <td>
                                {det.estado === 'creado' && <span className="badge-new">Creado</span>}
                                {det.estado === 'actualizado' && <span className="badge-changes">Actualizado</span>}
                                {det.estado === 'sin_cambios' && <span className="badge-ok">Sin cambios</span>}
                                {det.estado === 'error' && <span className="badge-error">Error</span>}
                              </td>
                              <td>-</td>
                              <td>-</td>
                              <td>{det.stock_total != null ? `${det.stock_total} uds` : '-'}</td>
                              <td className="detail-cell">
                                {det.estado === 'sin_cambios' && det.nota}
                                {det.estado === 'error' && <span className="error-text">{det.error}</span>}
                              </td>
                            </tr>
                          );
                        }
                        return lotes.map((lote, li) => (
                          <tr key={`${idx}-${li}`} className={`result-row ${det.estado}`}>
                            {li === 0 ? (
                              <>
                                <td rowSpan={lotes.length}><strong>{det.codigo}</strong></td>
                                <td rowSpan={lotes.length}>{det.nombre}</td>
                                <td rowSpan={lotes.length}>
                                  {det.estado === 'creado' && <span className="badge-new">Creado</span>}
                                  {det.estado === 'actualizado' && <span className="badge-changes">Actualizado</span>}
                                </td>
                              </>
                            ) : null}
                            <td>{lote.lote}</td>
                            <td><code className="ubicacion-code">{lote.ubicacion}</code></td>
                            <td className="num">{lote.stock_total} uds</td>
                            {li === 0 ? (
                              <td rowSpan={lotes.length} className="detail-cell">
                                {det.errores_lotes?.length > 0 && (
                                  <div className="errores-lotes-detail">
                                    {det.errores_lotes.map((err, ei) => (
                                      <div key={ei} className="error-lote-item">
                                        <XCircle size={12} />
                                        <span><strong>{err.lote}</strong>: {err.error}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sync-modal-footer">
            <button className="btn-cancel" onClick={handleCloseSyncModal}>
              {syncResult ? 'Cerrar' : 'Cancelar'}
            </button>
            {!syncResult && (
              <button
                className="btn-confirm-sync"
                onClick={handleConfirmSync}
                disabled={syncLoading || previewLoading}
              >
                {syncLoading
                  ? <><RefreshCw size={16} className="spin" /> Sincronizando...</>
                  : <><ArrowRight size={16} /> Confirmar Sincronización ({selectedProducts.length})</>
                }
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ======= RENDER PRINCIPAL =======
  return (
    <div className="productos-ab">
      {/* Header */}
      <div className="ab-list-header">
        <div className="ab-list-title">
          <Package size={20} />
          <h2>Sincronización - Productos</h2>
        </div>
        <div className="ab-list-actions">
          <button
            onClick={handleSyncExistentes}
            disabled={syncExistentesLoading || loading}
            className="btn-action green"
          >
            <Download size={14} />
            {syncExistentesLoading ? 'Sincronizando...' : 'Sync Existentes'}
          </button>
          <button
            onClick={loadProductosAB}
            disabled={loading}
            className="btn-action indigo"
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            {loading ? 'Cargando...' : 'Cargar desde AB'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {productosAB && (
        <div className="ab-stats">
          <div className="stat-card stat-total">
            <div className="stat-value">{productosAB.total_ab}</div>
            <div className="stat-label">Total AB</div>
          </div>
          <div className="stat-card stat-existing">
            <div className="stat-value">{productosAB.existentes}</div>
            <div className="stat-label">Existentes</div>
          </div>
          <div className="stat-card stat-pending">
            <div className="stat-value">{productosAB.pendientes}</div>
            <div className="stat-label">Pendientes</div>
          </div>
        </div>
      )}

      {/* Tabs + Sync button */}
      <div className="ab-tabs-row">
        <div className="ab-tabs">
          <button
            onClick={() => setFilterTab('pendientes')}
            className={`ab-tab ${filterTab === 'pendientes' ? 'active pending' : ''}`}
          >
            <AlertTriangle size={14} />
            Pendientes ({pendientes.length})
          </button>
          <button
            onClick={() => setFilterTab('existentes')}
            className={`ab-tab ${filterTab === 'existentes' ? 'active existing' : ''}`}
          >
            <CheckCircle size={14} />
            Existentes ({existentes.length})
          </button>
        </div>

        {selectedProducts.length > 0 && (
          <button className="btn-sync-selected" onClick={handleOpenSyncModal}>
            <ArrowRight size={14} />
            Sincronizar Seleccionados ({selectedProducts.length})
          </button>
        )}
      </div>

      {/* Search */}
      <div className="ab-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Buscar por código, nombre, proveedor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="ab-loading">
          <RefreshCw size={32} className="spin" />
          <p>Cargando productos desde AB...</p>
        </div>
      )}

      {/* No data */}
      {!loading && !productosAB && (
        <div className="ab-empty">
          <Package size={32} />
          <p>Pulsa "Cargar desde AB" para obtener productos</p>
        </div>
      )}

      {/* Pendientes list */}
      {!loading && filterTab === 'pendientes' && productosAB && (
        <div className="ab-product-list">
          {/* Select all bar */}
          {filteredPendientes.length > 0 && (
            <div className="select-all-bar">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                />
                <span>Seleccionar todos ({filteredPendientes.length})</span>
              </label>
              {selectedProducts.length > 0 && (
                <span className="selected-count">
                  {selectedProducts.length} seleccionados
                </span>
              )}
            </div>
          )}

          {filteredPendientes.length === 0 ? (
            <div className="ab-empty">
              <CheckCircle size={32} />
              <p>No hay productos pendientes de sincronizar</p>
            </div>
          ) : (
            filteredPendientes.map((prod, idx) => {
              const validation = getValidationStatus(prod);
              const isSelected = selectedProducts.includes(prod.codigo);
              return (
                <div key={idx} className={`ab-product-card pending ${isSelected ? 'selected' : ''}`}>
                  <div
                    className="ab-product-row"
                    onClick={() => setExpandedProduct(expandedProduct === prod.codigo ? null : prod.codigo)}
                  >
                    <div className="ab-product-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectProduct(prod.codigo)}
                      />
                    </div>
                    <div className="ab-product-info">
                      <div className="ab-product-top">
                        <span className="ab-code">{prod.codigo}</span>
                        <span className="ab-name">{prod.nombre}</span>
                        {validation.ok
                          ? <CheckCircle2 size={14} className="icon-ok" />
                          : <AlertTriangle size={14} className="icon-warn" />
                        }
                      </div>
                      <div className="ab-product-meta">
                        {(prod.codigo_barras_minima || prod.codigo_barras_mayor) && <span className="meta-tag barcode">BC: {prod.codigo_barras_minima || prod.codigo_barras_mayor}</span>}
                        {prod.jerarquia && prod.jerarquia.length > 0 && (
                          <span className="meta-tag units">
                            {prod.jerarquia.join('→')}
                          </span>
                        )}
                        {prod.proveedor && <span>Prov: {prod.proveedor}</span>}
                        {prod.familia && <span>Fam: {prod.familia}</span>}
                        {prod.almacen && <span>Alm: {prod.almacen}</span>}
                        <span>Lotes: {prod.lotes?.length || 0}</span>
                      </div>
                    </div>
                    <div className="ab-product-actions">
                      {expandedProduct === prod.codigo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>

                  {expandedProduct === prod.codigo && (
                    <div className="ab-product-detail">
                      <div className="ab-detail-grid">
                        <div><strong>BC Mayor:</strong> {prod.codigo_barras_mayor || <span className="missing-value">-</span>}</div>
                        <div><strong>BC Intermedia:</strong> {prod.codigo_barras_intermedia || <span className="missing-value">-</span>}</div>
                        <div><strong>BC Mínima:</strong> {prod.codigo_barras_minima || <span className="missing-value">-</span>}</div>
                        <div><strong>Jerarquía:</strong> {(prod.jerarquia || []).join(' → ')}</div>
                        {prod.unidad_mayor && <div><strong>Unidad Mayor:</strong> {prod.unidad_mayor}</div>}
                        {prod.unidad_display && <div><strong>Unidad Display:</strong> {prod.unidad_display}</div>}
                        <div><strong>Unidad Base:</strong> {prod.unidad_base || 'Unidad'}</div>
                        {prod.unidad_mayor && <div><strong>{prod.unidad_display ? 'Displays/Mayor' : 'Base/Mayor'}:</strong> {prod.displays_por_mayor}</div>}
                        {prod.unidad_display && <div><strong>Base/Display:</strong> {prod.unidades_por_display}</div>}
                        <div><strong>AB Original:</strong> <span style={{fontSize:'11px',color:'#64748b'}}>{prod.unidad_mayor_ab || '-'}</span></div>
                        <div><strong>Costo Promedio:</strong> {prod.costo_promedio}</div>
                        <div><strong>Ult. Precio Compra:</strong> {prod.ultimo_precio_compra}</div>
                        <div><strong>Comprometido:</strong> {prod.comprometido}</div>
                        <div><strong>Disponible:</strong> {prod.disponible}</div>
                        <div><strong>Subfamilia:</strong> {prod.subfamilia || '-'}</div>
                        <div><strong>Categoría AB:</strong> {prod.categoria_ab ?? '-'}</div>
                        <div><strong>Subcategoría AB:</strong> {prod.subcategoria_ab ?? '-'}</div>
                      </div>
                      {prod.lotes?.length > 0 && (
                        <div className="ab-lotes-table">
                          <strong>Lotes AB:</strong>
                          <table>
                            <thead>
                              <tr>
                                <th>Lote</th>
                                <th>Almacén</th>
                                {prod.unidad_mayor && <th>{prod.unidad_mayor}</th>}
                                {prod.unidad_display && <th>{prod.unidad_display}</th>}
                                <th>{prod.unidad_base || 'Base'}</th>
                                <th>Total</th>
                                <th>Vencimiento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {prod.lotes.map((l, li) => (
                                <tr key={li}>
                                  <td>{l.lote}</td>
                                  <td>{l.almacen}</td>
                                  {prod.unidad_mayor && <td className="num">{l.cantidad_mayor}</td>}
                                  {prod.unidad_display && <td className="num">{l.cantidad_intermedia}</td>}
                                  <td className="num">{l.cantidad_minima}</td>
                                  <td className="num"><strong>{l.cantidad_total}</strong></td>
                                  <td>{l.fecha_expiracion || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {!validation.ok && (
                        <div className="validation-warnings">
                          <AlertTriangle size={12} />
                          <span>Campos faltantes: {validation.issues.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Existentes list */}
      {!loading && filterTab === 'existentes' && productosAB && (
        <div className="ab-product-list">
          {/* Select all bar */}
          {filteredExistentes.length > 0 && (
            <div className="select-all-bar">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                />
                <span>Seleccionar todos ({filteredExistentes.length})</span>
              </label>
              {selectedProducts.length > 0 && (
                <span className="selected-count">
                  {selectedProducts.length} seleccionados
                </span>
              )}
            </div>
          )}

          {filteredExistentes.length === 0 ? (
            <div className="ab-empty">
              <Package size={32} />
              <p>No hay productos existentes</p>
            </div>
          ) : (
            filteredExistentes.map((prod, idx) => {
              const isSelected = selectedProducts.includes(prod.codigo);
              return (
                <div key={idx} className={`ab-product-card existing ${isSelected ? 'selected' : ''}`}>
                  <div className="ab-product-row">
                    <div className="ab-product-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectProduct(prod.codigo)}
                      />
                    </div>
                    <div className="ab-product-info">
                      <div className="ab-product-top">
                        <CheckCircle size={14} style={{ color: '#16a34a' }} />
                        <span className="ab-code existing">{prod.codigo}</span>
                        <span className="ab-name">{prod.nombre}</span>
                      </div>
                      <div className="ab-product-meta">
                        {(prod.codigo_barras_minima || prod.codigo_barras_mayor) && <span className="meta-tag barcode">BC: {prod.codigo_barras_minima || prod.codigo_barras_mayor}</span>}
                        {prod.proveedor && <span>Prov: {prod.proveedor}</span>}
                        {prod.familia && <span>Fam: {prod.familia}</span>}
                        {prod.producto_local && <span>ID Local: {prod.producto_local.id}</span>}
                        <span>Lotes WMS: {prod.producto_local?.stock_lotes?.lotes?.length || 0}</span>
                        <span>Lotes AB: {prod.lotes?.length || 0}</span>
                      </div>
                    </div>
                    <span className="ab-synced-badge">Sincronizado</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal de sincronización */}
      {renderSyncModal()}

      <style jsx>{`
        .productos-ab { padding: 0; }

        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* HEADER */
        .ab-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .ab-list-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ab-list-title svg { color: #6366f1; }

        .ab-list-title h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .ab-list-actions {
          display: flex;
          gap: 8px;
        }

        .btn-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: white;
          transition: all 0.2s;
        }

        .btn-action.green { background: #10b981; }
        .btn-action.green:hover { background: #059669; }
        .btn-action.indigo { background: #6366f1; }
        .btn-action.indigo:hover { background: #4f46e5; }
        .btn-action:disabled { opacity: 0.6; cursor: not-allowed; }

        /* STATS */
        .ab-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
          padding: 0 24px 16px;
        }

        .stat-card {
          border-radius: 8px;
          padding: 12px;
          text-align: center;
        }

        .stat-total { background: #F0F9FF; border: 1px solid #BAE6FD; }
        .stat-existing { background: #F0FDF4; border: 1px solid #BBF7D0; }
        .stat-pending { background: #FFFBEB; border: 1px solid #FDE68A; }

        .stat-value { font-size: 24px; font-weight: bold; }
        .stat-total .stat-value { color: #0284C7; }
        .stat-existing .stat-value { color: #16A34A; }
        .stat-pending .stat-value { color: #D97706; }
        .stat-label { font-size: 12px; color: #64748B; }

        /* TABS ROW */
        .ab-tabs-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 24px;
          border-bottom: 2px solid #E2E8F0;
          flex-wrap: wrap;
          gap: 8px;
        }

        .ab-tabs {
          display: flex;
          gap: 4px;
        }

        .ab-tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border: none;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          background: transparent;
          color: #64748B;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          border-radius: 6px 6px 0 0;
          transition: all 0.2s;
        }

        .ab-tab:hover { color: #3B82F6; }
        .ab-tab.active.pending { color: #D97706; border-bottom-color: #D97706; background: white; }
        .ab-tab.active.existing { color: #16A34A; border-bottom-color: #16A34A; background: white; }

        .btn-sync-selected {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          background: linear-gradient(135deg, #3B82F6, #6366F1);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .btn-sync-selected:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        /* SEARCH */
        .ab-search {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 16px 24px;
          padding: 10px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          background: white;
        }

        .ab-search svg { color: #94A3B8; flex-shrink: 0; }

        .ab-search input {
          border: none;
          outline: none;
          font-size: 13px;
          flex: 1;
          color: #1A1A1A;
        }

        /* LOADING / EMPTY */
        .ab-loading, .ab-empty {
          text-align: center;
          padding: 40px;
          color: #64748B;
        }

        .ab-loading p, .ab-empty p {
          margin-top: 12px;
          font-size: 14px;
        }

        /* SELECT ALL BAR */
        .select-all-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          margin-bottom: 8px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #6366f1;
          cursor: pointer;
        }

        .selected-count {
          font-size: 12px;
          color: #6366f1;
          font-weight: 600;
          background: #EEF2FF;
          padding: 4px 10px;
          border-radius: 20px;
        }

        /* PRODUCT LIST */
        .ab-product-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 0 24px 24px;
        }

        .ab-product-card {
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .ab-product-card.pending {
          border: 1px solid #FDE68A;
          background: #FFFBEB;
        }

        .ab-product-card.pending.selected {
          border-color: #818CF8;
          background: #EEF2FF;
        }

        .ab-product-card.existing {
          border: 1px solid #BBF7D0;
          background: #F0FDF4;
        }

        .ab-product-row {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          gap: 12px;
        }

        .ab-product-check {
          flex-shrink: 0;
        }

        .ab-product-check input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #6366f1;
          cursor: pointer;
        }

        .ab-product-info { flex: 1; min-width: 0; }

        .ab-product-top {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }

        .ab-code {
          font-family: monospace;
          font-size: 12px;
          background: #FEF3C7;
          padding: 2px 6px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .ab-code.existing { background: #DCFCE7; }

        .ab-name {
          font-weight: 600;
          font-size: 14px;
          color: #1A1A1A;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .icon-ok { color: #16a34a; }
        .icon-warn { color: #d97706; }
        .icon-skip { color: #6366f1; }
        .icon-error { color: #dc2626; }

        .ab-product-meta {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #64748B;
          flex-wrap: wrap;
        }

        .meta-tag {
          padding: 1px 6px;
          border-radius: 4px;
          font-weight: 500;
        }

        .meta-tag.barcode {
          background: #DBEAFE;
          color: #1D4ED8;
        }

        .meta-tag.units {
          background: #F3E8FF;
          color: #7C3AED;
        }

        .ab-product-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #94A3B8;
        }

        .ab-synced-badge {
          font-size: 12px;
          color: #16A34A;
          font-weight: 600;
        }

        /* DETAIL EXPANDED */
        .ab-product-detail {
          padding: 0 16px 12px;
          border-top: 1px solid #E2E8F0;
        }

        .ab-detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
          padding: 12px 0;
          font-size: 12px;
          color: #334155;
        }

        .missing-value {
          color: #DC2626;
          font-style: italic;
        }

        .ab-lotes-table { font-size: 12px; margin-top: 8px; }
        .ab-lotes-table strong { display: block; margin-bottom: 4px; }

        .ab-lotes-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .ab-lotes-table th {
          background: #F1F5F9;
          padding: 4px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 11px;
        }

        .ab-lotes-table td {
          padding: 4px 8px;
          border-bottom: 1px solid #E2E8F0;
        }

        .ab-lotes-table td.num { text-align: right; font-family: monospace; }

        .validation-warnings {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          padding: 6px 10px;
          background: #FEF3C7;
          border-radius: 6px;
          font-size: 12px;
          color: #92400E;
        }

        /* ======= MODAL DE SINCRONIZACIÓN ======= */
        .sync-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .sync-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          animation: modalSlide 0.3s ease-out;
        }

        @keyframes modalSlide {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .sync-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #E5E7EB;
        }

        .sync-modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748B;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .modal-close:hover { background: #F1F5F9; color: #1A1A1A; }

        .sync-modal-tabs {
          display: flex;
          gap: 4px;
          padding: 12px 24px 0;
          border-bottom: 2px solid #E2E8F0;
        }

        .modal-tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border: none;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          background: transparent;
          color: #64748B;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          border-radius: 6px 6px 0 0;
          transition: all 0.2s;
        }

        .modal-tab:hover { color: #3B82F6; }
        .modal-tab.active { color: #3B82F6; border-bottom-color: #3B82F6; background: #F8FAFC; }
        .modal-tab.debug.active { color: #7C3AED; border-bottom-color: #7C3AED; background: #FAF5FF; }

        .sync-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 16px 24px;
        }

        /* PREVIEW ITEMS */
        .preview-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .preview-item {
          border: 1px solid #E5E7EB;
          border-radius: 10px;
          padding: 16px;
          transition: all 0.2s;
        }

        .preview-item.ok { border-left: 4px solid #16A34A; }
        .preview-item.warning { border-left: 4px solid #D97706; }

        .preview-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .preview-item-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .preview-code {
          font-family: monospace;
          font-size: 12px;
          background: #EEF2FF;
          color: #6366f1;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 600;
        }

        .preview-name {
          font-weight: 600;
          font-size: 14px;
          color: #1A1A1A;
        }

        .preview-lotes-badge {
          font-size: 12px;
          background: #DBEAFE;
          color: #1D4ED8;
          padding: 3px 10px;
          border-radius: 20px;
          font-weight: 500;
        }

        .preview-item-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
          margin-bottom: 8px;
        }

        .preview-field {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .field-label {
          font-size: 11px;
          font-weight: 600;
          color: #94A3B8;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .field-value {
          font-size: 13px;
          color: #334155;
        }

        .field-value.missing {
          color: #DC2626;
          font-style: italic;
        }

        .preview-lotes { margin-top: 8px; }

        .preview-lotes table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .preview-lotes th {
          background: #F1F5F9;
          padding: 4px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 11px;
        }

        .preview-lotes td {
          padding: 4px 8px;
          border-bottom: 1px solid #F1F5F9;
        }

        .preview-lotes td.num { text-align: right; font-family: monospace; }

        .preview-warnings {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          padding: 6px 10px;
          background: #FEF3C7;
          border-radius: 6px;
          font-size: 12px;
          color: #92400E;
        }

        /* DEBUG */
        .debug-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .debug-info-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #FAF5FF;
          border: 1px solid #E9D5FF;
          border-radius: 8px;
          font-size: 13px;
          color: #6B21A8;
        }

        .debug-product-selector {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .debug-product-selector label {
          font-size: 13px;
          font-weight: 600;
          color: #475569;
        }

        .debug-product-selector select {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          font-size: 13px;
          color: #1A1A1A;
          background: white;
        }

        .debug-json {
          background: #1E293B;
          color: #E2E8F0;
          padding: 16px;
          border-radius: 10px;
          font-size: 12px;
          line-height: 1.6;
          overflow-x: auto;
          max-height: 500px;
          overflow-y: auto;
          font-family: 'Consolas', 'SF Mono', 'Fira Code', monospace;
          white-space: pre;
          tab-size: 2;
        }

        /* SYNC RESULT */
        .sync-result {
          animation: fadeIn 0.3s ease-out;
        }

        .sync-result-header {
          margin-bottom: 16px;
        }

        .sync-result-header h3 {
          margin: 0 0 10px;
          font-size: 16px;
          color: #1A1A1A;
        }

        .sync-result-stats {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .result-stat {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .result-stat.ok { background: #F0FDF4; color: #16A34A; border: 1px solid #BBF7D0; }
        .result-stat.updated { background: #EFF6FF; color: #2563EB; border: 1px solid #BFDBFE; }
        .result-stat.skip { background: #EEF2FF; color: #6366F1; border: 1px solid #C7D2FE; }
        .result-stat.error { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }

        .sync-result-table-container {
          overflow-x: auto;
        }

        .sync-result-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .sync-result-table th {
          background: #F1F5F9;
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          color: #475569;
          border-bottom: 2px solid #E2E8F0;
          white-space: nowrap;
        }

        .sync-result-table td {
          padding: 7px 12px;
          border-bottom: 1px solid #E2E8F0;
          vertical-align: middle;
        }

        .result-row.creado td { background: #F0FDF4; }
        .result-row.actualizado td { background: #EFF6FF; }
        .result-row.sin_cambios td { background: #F8FAFC; }
        .result-row.error td { background: #FEF2F2; }

        .ubicacion-code {
          background: #EEF2FF;
          color: #4F46E5;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .detail-cell { font-size: 12px; color: #64748B; }

        .errores-lotes-detail {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .error-lote-item {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          color: #DC2626;
          font-size: 11px;
        }

        .error-lote-item svg { flex-shrink: 0; margin-top: 1px; }

        .badge-new { background: #DCFCE7; color: #16A34A; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
        .badge-changes { background: #DBEAFE; color: #2563EB; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
        .badge-ok { background: #F1F5F9; color: #64748B; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
        .badge-error { background: #FEE2E2; color: #DC2626; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }

        .error-text { color: #DC2626; }

        /* MODAL FOOTER */
        .sync-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 24px;
          border-top: 1px solid #E5E7EB;
          background: #F8FAFC;
          border-radius: 0 0 16px 16px;
        }

        .btn-cancel {
          padding: 10px 20px;
          background: #F1F5F9;
          color: #475569;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-cancel:hover { background: #E2E8F0; }

        .btn-confirm-sync {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: linear-gradient(135deg, #3B82F6, #6366F1);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .btn-confirm-sync:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .btn-confirm-sync:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* PREVIEW TYPE BADGES */
        .preview-type-badge {
          font-size: 11px;
          padding: 3px 10px;
          border-radius: 20px;
          font-weight: 600;
        }
        .preview-type-badge.nuevo { background: #F0FDF4; color: #16A34A; border: 1px solid #BBF7D0; }
        .preview-type-badge.existente { background: #EFF6FF; color: #2563EB; border: 1px solid #BFDBFE; }

        .preview-item.existing { border-left: 4px solid #3B82F6; }

        .icon-new { color: #16A34A; }
        .icon-edit { color: #2563EB; }

        /* COMPARISON */
        .preview-comparison { margin-top: 8px; }
        .comparison-summary {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: #64748B;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .comp-row.cambios { background: #FFFBEB; }
        .comp-row.nuevo { background: #F0FDF4; }
        .comp-row.sin_cambios { background: transparent; }

        .arrow-cell { text-align: center; color: #94A3B8; }

        .badge-new {
          display: inline-block;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          background: #DCFCE7;
          color: #16A34A;
        }
        .badge-changes {
          display: inline-block;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          background: #FEF3C7;
          color: #D97706;
        }
        .badge-ok {
          display: inline-block;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          background: #F1F5F9;
          color: #64748B;
        }

        .comp-msg {
          font-size: 11px;
          color: #64748B;
          max-width: 200px;
        }

        /* STRATEGY SELECTOR */
        .strategy-selector {
          margin-top: 16px;
          padding: 16px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
        }

        .strategy-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          font-size: 13px;
          color: #334155;
        }

        .strategy-options {
          display: flex;
          gap: 12px;
        }

        .strategy-option {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .strategy-option:hover { border-color: #93C5FD; }

        .strategy-option.active {
          border-color: #3B82F6;
          background: #EFF6FF;
        }

        .strategy-option input[type="radio"] {
          margin-top: 3px;
          accent-color: #3B82F6;
        }

        .strategy-option div {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .strategy-option strong {
          font-size: 13px;
          color: #1A1A1A;
        }

        .strategy-option span {
          font-size: 11px;
          color: #64748B;
        }

        .ab-product-card.existing.selected {
          border-color: #818CF8;
          background: #EEF2FF;
        }

        .preview-lotes th { font-size: 11px; white-space: nowrap; }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .ab-list-header { flex-direction: column; align-items: flex-start; }
          .ab-tabs-row { flex-direction: column; }
          .preview-item-grid { grid-template-columns: 1fr; }
          .sync-modal { max-width: 100%; margin: 10px; }
          .ab-detail-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ProductosAB;
